import _isEmpty from "lodash.isempty";
import { format as dateFormat, isValid as isValidDate, parse } from "date-fns";
import { patRelOptions } from "./pi1-form-content";
import { FI_TYPES } from "./pi1-form-constants";
import { isValidType, isValidLoanAmount } from "./pi1-form-validation";

const getRelOptions = (rel) => {
  if (rel) {
    const option = patRelOptions.find(
      (option) => option.name.toLowerCase() === rel,
    ) || { val: "6" };
    return option.val;
  }
  return "6";
};

const determineDateFormat = (dateStr) => {
  if (!dateStr || Number.isNaN(dateStr)) {
    return;
  }

  const monthDayYearRegex =
    /^(0?[1-9]|1[0-2])\/(0?[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/;
  const yearMonthDayWithDashesRegex =
    /^((?:19|20)\d{2})\-(0?[1-9]|1[0-2])\-((?:0?\[1-9])|(?:1|2)[1-9]|3[01])$/;

  if (monthDayYearRegex.test(dateStr)) {
    return "MM/dd/yyyy";
  }

  if (yearMonthDayWithDashesRegex.test(dateStr)) {
    return "yyyy-MM-dd";
  }
};

const parseDate = (dateStr) => {
  const format = determineDateFormat(dateStr);
  try {
    const parsedDate = format && parse(dateStr, format, new Date());
    return isValidDate(parsedDate) ? dateFormat(parsedDate, "MM/dd/yyyy") : "";
  } catch {
    return "";
  }
};

const setApplicantName = (rel, applicantName, patientName) => {
  let name = "";

  if (rel && rel.toLowerCase() !== "self" && applicantName) {
    name = applicantName;
  } else if (patientName) {
    name = patientName;
  }

  return name;
};

const isApplicantNameValid = (rel, applicantName, patientName) => {
  const name =
    rel && rel.toLowerCase() === "self" ? patientName : applicantName;
  return !_isEmpty(name);
};

export const getFormMetadata = (
  userInfo,
  defaultValues,
  clientProducts,
  inEmailVerPilot = false,
  shouldCollectEmploymentStatus = false,
) => {
  const APPLICANT = "applicant",
    // fallbacks for 'user' and 'isLoggedIn' needed for double postback
    user = userInfo.userType || APPLICANT,
    isLoggedIn = userInfo.isLoggedIn || false,
    // Email is only a required field when an applicant is not logged in
    // Provider lead flows, including Corp, Admin, Super Admin don't require the field
    // - though the field is shown
    // NOTE: There is a scenario when Applicant has been logged in, and reaches PI3 page,
    //   but then selects to start new application, in this scenario,
    //   PI1 form render logic hides the email field, and here we dont require
    isApplicantLoggedOut = user === APPLICANT && !isLoggedIn,
    isEmailRequired = inEmailVerPilot || isApplicantLoggedOut ? true : false,
    isEmailValid = isEmailRequired ? false : true, // set init state true if not req
    isPasswordRequired = isApplicantLoggedOut ? true : false;


  const AUTO_CMPL_OFF = "disabled",
    setAutoComplete = (fieldStr) =>
      user === APPLICANT && fieldStr ? fieldStr : AUTO_CMPL_OFF;
  const getEsignConfig = () => {
    const eSign = {
      isRequired: true,
      isValid: defaultValues.esign || false,
      status: "",
      value: defaultValues.esign || false,
    };
    return eSign;
  };
  return {
    sectionAmount: {
      isValid: false,
      status: "",
      fields: {
        loanAmount: {
          isRequired: true,
          isValid: isValidLoanAmount(
            defaultValues.epa,
            defaultValues.state,
            clientProducts,
          ),
          status: "",
          value: defaultValues.epa || "",
          autocomplete: setAutoComplete(AUTO_CMPL_OFF), // explicit disable
        },
        recipientFN: {
          // recipient first name
          isRequired: true,
          isValid: !_isEmpty(defaultValues.pfn),
          status: "",
          value: (defaultValues.pfn && defaultValues.pfn.trim()) || "",
          autocomplete: setAutoComplete("given-name"),
        },
        recipientLN: {
          // recipient last name
          isRequired: true,
          isValid: !_isEmpty(defaultValues.pln),
          status: "",
          value: (defaultValues.pln && defaultValues.pln.trim()) || "",
          autocomplete: setAutoComplete("family-name"),
        },
        relationship: {
          // recipient's relationship to applicant
          isRequired: false,
          isValid: true,
          status: "",
          value: getRelOptions(defaultValues.rel),
        },
      },
    },
    sectionAddress: {
      isValid: false,
      status: "",
      fields: {
        applicantFN: {
          // applicant first name
          isRequired: false,
          isValid: isApplicantNameValid(
            defaultValues.rel,
            defaultValues.afn,
            defaultValues.pfn,
          ),
          status: "",
          value: setApplicantName(
            defaultValues.rel,
            defaultValues.afn,
            defaultValues.pfn,
          ),
          autocomplete: setAutoComplete("given-name"),
        },
        applicantLN: {
          // applicant last name
          isRequired: false,
          isValid: isApplicantNameValid(
            defaultValues.rel,
            defaultValues.aln,
            defaultValues.pln,
          ),
          status: "",
          value: setApplicantName(
            defaultValues.rel,
            defaultValues.aln,
            defaultValues.pln,
          ),
          autocomplete: setAutoComplete("family-name"),
        },
        address: {
          home: {
            street: {
              isRequired: true,
              isValid: !_isEmpty(defaultValues.addr1),
              status: "",
              value: defaultValues.addr1 || null,
              autocomplete: setAutoComplete(
                "section-shipping shipping address-line1",
              ),
              autofill: defaultValues.isAutoFill || false,
            },
            aptsuite: {
              isRequired: false,
              isValid: false,
              status: "",
              value: defaultValues.addr2 || null,
              autocomplete: setAutoComplete(
                "section-shipping shipping address-line2",
              ),
            },
            city: {
              isRequired: true,
              isValid: !_isEmpty(defaultValues.city),
              status: "",
              value: defaultValues.city || null,
              autocomplete: setAutoComplete(
                "section-shipping shipping address-level2",
              ),
            },
            state: {
              isRequired: true,
              isValid: !_isEmpty(defaultValues.state),
              status: "",
              value: defaultValues.state || null,
              autocomplete: setAutoComplete(
                "section-shipping shipping address-level1",
              ),
            },
            zip: {
              isRequired: true,
              isValid: isValidType(defaultValues.zip, FI_TYPES.ZIP),
              status: "",
              value: defaultValues.zip || null,
              autocomplete: setAutoComplete(
                "section-shipping shipping postal-code",
              ),
            },
          },
          mail: {
            street: {
              isRequired: false,
              isValid: false,
              status: "",
              value: null,
              autocomplete: setAutoComplete(
                "section-billing billing address-line1",
              ),
            },
            aptsuite: {
              isRequired: false,
              isValid: false,
              status: "",
              value: null,
              autocomplete: setAutoComplete(
                "section-billing billing address-line2",
              ),
            },
            city: {
              isRequired: false,
              isValid: false,
              status: "",
              value: null,
              autocomplete: setAutoComplete(
                "section-billing billing address-level2",
              ),
            },
            state: {
              isRequired: false,
              isValid: false,
              status: "",
              value: null,
              autocomplete: setAutoComplete(
                "section-billing billing address-level1",
              ),
            },
            zip: {
              isRequired: false,
              isValid: false,
              status: "",
              value: null,
              autocomplete: setAutoComplete(
                "section-billing billing postal-code",
              ),
            },
          },
          isSameAddr: "Yes",
        },
      },
    },
    sectionDetails: {
      isValid: false,
      status: "",
      fields: {
        phone: {
          isRequired: true,
          isValid: isValidType(defaultValues.phone, FI_TYPES.PHONE_NUM),
          status: "",
          value: defaultValues.phone || "",
          autocomplete: setAutoComplete("tel"),
        },
        email: {
          isRequired: isEmailRequired,
          isValid: !_isEmpty(defaultValues.email, FI_TYPES.EMAIL)
            ? isValidType(defaultValues.email, FI_TYPES.EMAIL)
            : isEmailValid,
          status: "",
          value: defaultValues.email || "",
          autocomplete: setAutoComplete("email"),
        },
        emailConfirmation: {
          isRequired: inEmailVerPilot,
          isValid: inEmailVerPilot ? false : true,
          status: "",
          value: defaultValues.emailConfirmation || "",
        },
        password: {
          isRequired: isPasswordRequired,
          isValid:
            (defaultValues.password &&
              isValidType(defaultValues.password, FI_TYPES.PASSWORD)) ||
            false,
          status: "",
          value: defaultValues.password || "",
          autocomplete: setAutoComplete("new-password"),
        },
        dob: {
          isRequired: true,
          isValid: isValidType(defaultValues.dob, FI_TYPES.MMDDYYYY),
          status: "",
          value: parseDate(defaultValues.dob),
          // 'bday' is the correct autocomplete value,
          // but setting 'disabled' instead
          // autofill uses customer provided data which
          // may not be in the desired format for this field
          autocomplete: setAutoComplete(AUTO_CMPL_OFF),
        },
        ssn: {
          isRequired: true,
          isValid:
            defaultValues.ssn && !isNaN(defaultValues.ssn) ? true : false,
          status: "",
          value: defaultValues.ssn || "",
          autocomplete: AUTO_CMPL_OFF,
        },
        ...(shouldCollectEmploymentStatus && {
          employmentStatus: {
            isRequired: true,
            isValid: !!defaultValues.employmentStatus,
            status: "",
            value: defaultValues.employmentStatus || "",
          },
          employerName: {
            isRequired: true,
            isValid: !!defaultValues.employerName,
            status: "",
            value: defaultValues.employerName || "",
          },
          jobTitle: {
            isRequired: true,
            isValid: !!defaultValues.jobTitle,
            status: "",
            value: defaultValues.jobTitle || "",
          }
        }),
        income: {
          isRequired: true,
          isValid:
            defaultValues.gyii && !isNaN(defaultValues.gyii) ? true : false,
          status: "",
          value: defaultValues.gyii || "",
          type: "salary",
          autocomplete: setAutoComplete(AUTO_CMPL_OFF),
        },
        rent: {
          isRequired: true,
          isValid:
            defaultValues.mmr && !isNaN(defaultValues.mmr) ? true : false,
          status: "",
          value: defaultValues.mmr || "",
          autocomplete: setAutoComplete(AUTO_CMPL_OFF),
        },
      },
    },
    sectionDisclosures: {
      isValid: false,
      status: "",
      fields: {
        esign: getEsignConfig(),
        creditauth: {
          isRequired: true,
          isValid: defaultValues.creditauth || false,
          status: "",
          value: defaultValues.creditauth || false,
        },
        privpolicy: {
          isRequired: true,
          isValid: defaultValues.privpolicy || false,
          status: "",
          value: defaultValues.privpolicy || false,
        },
      },
    },
  };
};

require('/var/lib/jenkins/workspace/pf-lcps-ui_Release_0/src/pages/borrower-registration/styles/borrower-registration.scss');